import React, { useRef, useEffect } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { formatDistance } from 'date-fns';
import { Icon } from 'leaflet';
import { Position } from '../../hooks/useWebSocket';
import { Session } from '../../types/session';

interface SessionMarkerProps {
  session: Session;
  position: Position;
  onClick?: () => void;
  isSelected?: boolean;
}

const selectedIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const defaultIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export const SessionMarker: React.FC<SessionMarkerProps> = ({
  session,
  position,
  onClick,
  isSelected = false,
}) => {
  const markerRef = useRef(null);

  // Open popup when selected
  useEffect(() => {
    if (isSelected && markerRef.current) {
      (markerRef.current as any).openPopup();
    }
  }, [isSelected]);
  const timestampText = formatDistance(
    new Date(position.timestamp),
    new Date(),
    { addSuffix: true }
  );

  const popupContent = (
    <div className="w-64 space-y-2">
      <div className="font-bold text-lg">
        {session.transportId} / {session.journeyId}
      </div>
      <div className="text-sm">
        <div className="font-semibold">{session.licensePlate}</div>
        <div className="text-gray-600">Driver: {session.driverId}</div>
        <div className="text-gray-600">Bus: {session.busId}</div>
      </div>
      <hr />
      <div className="text-xs space-y-1">
        <div>
          <span className="font-semibold">Last Update:</span> {timestampText}
        </div>
        <div>
          <span className="font-semibold">Coordinates:</span> {position.latitude.toFixed(5)}, {position.longitude.toFixed(5)}
        </div>
        {position.accuracy && (
          <div>
            <span className="font-semibold">Accuracy:</span> {position.accuracy.toFixed(0)} m
          </div>
        )}
        {position.speed !== undefined && (
          <div>
            <span className="font-semibold">Speed:</span> {(position.speed * 3.6).toFixed(1)} km/h
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Marker
      ref={markerRef}
      position={[position.latitude, position.longitude]}
      icon={isSelected ? selectedIcon : defaultIcon}
      eventHandlers={{ click: onClick }}
    >
      <Popup>{popupContent}</Popup>
    </Marker>
  );
};
